import type { RasterFormat } from '../core/export/download';
import type { StudioDocument } from '../store/studioStore';

export type BatchStrategy = 'random' | 'grid' | 'hypercube' | 'seedSweep' | 'mutation' | 'morph';

export const STRATEGY_LABELS: Record<BatchStrategy, string> = {
  random: 'Zufall',
  grid: 'Raster / Sweep',
  hypercube: 'Latin Hypercube',
  seedSweep: 'Seed-Durchlauf',
  mutation: 'Mutation',
  morph: 'Interpolation',
};

export const STRATEGY_HINTS: Record<BatchStrategy, string> = {
  random: 'Jeder freie Parameter wird unabhängig aus seinem Bereich gezogen.',
  grid: 'Ein bis drei Parameter als Achsen, vollständig durchkombiniert.',
  hypercube: 'Geschichtete Stichprobe — deckt den Parameterraum gleichmässiger ab als reiner Zufall.',
  seedSweep: 'Alles fix, nur der Seed läuft durch.',
  mutation: 'Ausgehend von einem Favoriten, mit einstellbarer Mutationsstärke.',
  morph: 'Zwischenschritte zwischen dem aktuellen Stand und einem gespeicherten Preset.',
};

export interface GridAxis {
  dimensionId: string;
  steps: number;
}

export type PostMode = 'off' | 'on' | 'random';

export interface BatchConfig {
  strategy: BatchStrategy;
  count: number;

  // ── What varies besides parameters ───────────────────────────────────────
  varyText: boolean;
  words: string[];
  varyFonts: boolean;
  fontIds: string[];
  varyCanvas: boolean;
  canvasPresetIds: string[];
  varyPalettes: boolean;
  paletteIds: string[];

  /** Techniques allowed to appear, with relative weights. */
  techniqueIds: string[];
  techniqueWeights: Record<string, number>;
  /** Recombine layers per variant instead of keeping the studio's stack. */
  combineLayers: boolean;
  minLayers: number;
  maxLayers: number;

  varyBackground: boolean;
  backgroundKinds: string[];

  postMode: Record<string, PostMode>;
  postProbability: number;

  seedFrom: number;
  seedTo: number;

  // ── Which parameter dimensions participate ───────────────────────────────
  includeTextParams: boolean;
  includeLayerParams: boolean;
  includeBackgroundParams: boolean;
  includePostParams: boolean;

  gridAxes: GridAxis[];

  mutationStrength: number;
  /** Variant the mutation strategy starts from; falls back to the studio document. */
  mutationBaseId: string | null;
  /** Preset the morph strategy ends at. */
  morphTargetPresetId: string | null;

  // ── Rendering ────────────────────────────────────────────────────────────
  /** Longest edge of the gallery thumbnails, in px. */
  previewSize: number;
  exportScale: number;
  format: RasterFormat;
  quality: number;
  /** Simulation-step budget drawn per variant, for techniques that never finish. */
  stepRange: [number, number];
  workers: number;
}

export interface VariantMeta {
  index: number;
  seed: number;
  word: string;
  fontId: string;
  techniques: string[];
  palette: string;
  canvas: string;
  /** The sampled dimension values, for the manifest and the diff view. */
  dimensions: Record<string, string | number | boolean>;
}

export interface Variant {
  id: string;
  meta: VariantMeta;
  doc: StudioDocument;
  /** Thumbnail; null while queued or after a failure. */
  blob: Blob | null;
  url: string | null;
  width: number;
  height: number;
  ms: number;
  elements: number;
  favourite: boolean;
  hidden: boolean;
  error: string | null;
}

export type BatchStatus = 'idle' | 'running' | 'paused' | 'done' | 'cancelled';

export interface BatchProgress {
  status: BatchStatus;
  total: number;
  completed: number;
  failed: number;
  /** Milliseconds since the run started. */
  elapsed: number;
  /** Estimated milliseconds remaining, or null before the first result. */
  remaining: number | null;
  currentTechnique: string;
}
